<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { getCssVar } from 'quasar'
import dayjs from 'dayjs'

import { menuRoutes, menuRouteEnum } from '@/router'
import { usePlanningStore } from '@/stores/planning.store'
import { serviceTypeIcons as SERVICE_TYPE_ICONS, usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useChatStore } from '@/stores/chat.store'
import {
  ABSENCE_COLOR,
  ABSENCE_ICON,
  PROJECT_FALLBACK_COLOR,
  eventTypeEnum,
  timelineDragModeEnum,
  timelineConflictResolutionEnum,
  timelineRowKindEnum,
} from '@/stores/schedule.store'
import { signalToneColor, signalToneEnum } from '@/composables/dashboard-kpi'
import { formatDecimal } from '@/composables/currency'
import { includesQuery, useProjectTimelineData, useServiceSearch } from '@/composables/project-timeline'
import { useAllocationConfirm } from '@/composables/allocation-confirm'
import { useTimelinePan } from '@/composables/timeline-pan'
import {
  CENTER_VIEWPORT_RATIO,
  HOUR_LINES_MIN_WIDTH,
  MINUTES_PER_DAY,
  TODAY_VIEWPORT_RATIO,
  useTimelineViewport,
} from '@/composables/timeline-viewport'
import { HIGHLIGHT_DURATION_MS, useHighlightNewRow } from '@/composables/scroll'
import { useKeydown } from '@/composables/keyboard'
import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import ProjectTimelineScale from '@/components/planner/project-timeline/ProjectTimelineScale.vue'
import ProjectTimelineEventDialog from '@/components/planner/project-timeline/ProjectTimelineEventDialog.vue'
import ProjectTimelineConflictDialog from '@/components/planner/project-timeline/ProjectTimelineConflictDialog.vue'
import ProjectTimelineReplaceDialog from '@/components/planner/project-timeline/ProjectTimelineReplaceDialog.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import DeleteOptionsDialog from '@/components/shared/DeleteOptionsDialog.vue'
import ProjectRescheduleDialogs from '@/components/shared/ProjectRescheduleDialogs.vue'
import ListItem from '@/components/shared/ListItem.vue'
import SearchInput from '@/components/shared/SearchInput.vue'
import NoStockConfirmDialog from '@/components/shared/NoStockConfirmDialog.vue'
import AbsenceConflictDialog from '@/components/shared/AbsenceConflictDialog.vue'
import AllocationConfirmDialog from '@/components/planner/AllocationConfirmDialog.vue'
import GroupComponentsDialog from '@/components/planner/GroupComponentsDialog.vue'

const props = defineProps({
  draggedService: {
    type: Object,
    default: null,
  },
  projectIds: {
    type: Array,
    default: null,
  },
  initialDate: {
    type: String,
    default: null,
  },
  // { projectId, serviceId, date }: the allocation to bring into view once loaded (serviceId may be null).
  focus: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['update:draggedService', 'select-project-listing'])

const { t, locale } = useI18n()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const chatStore = useChatStore()
const router = useRouter()
const data = useProjectTimelineData()
const { matchesServiceSearch } = useServiceSearch()
const { noStockDialog, confirmAllocationAsync } = useAllocationConfirm()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const { notifyWarning } = useNotify()
const { isMobile } = useDetectDevice()

const { PROJECT, SERVICE, EVENTS, ABSENCES } = timelineRowKindEnum
const { MOVE, RESIZE_START, RESIZE_END } = timelineDragModeEnum
const { ALLOCATION, ABSENCE } = eventTypeEnum
const { STAFF, EQUIPMENT, GROUP } = portalStore.serviceTypeEnum
const { projectBoundsActionEnum } = planningStore
const { SHIFT_ALLOCATION, DELETE_ALLOCATION, DELETE_ABSENCE } = timelineConflictResolutionEnum

const MIN_BAR_WIDTH = 0.375
const INITIAL_SPAN_DAYS = 60
const TASK_LIST_BOUNDS = Object.freeze({ min: 20, max: 50 })
const COMPACT_TASK_LIST_WIDTH = 5.25
const FOCUS_START_MARGIN_RATIO = 0.05
const PERCENT = 100
const DRAG_THRESHOLD = 3
const DATE_FORMAT = 'YYYY-MM-DD'
const warningColor = signalToneColor(signalToneEnum.ATTENTION)
const SERVICE_TYPE_ORDER = Object.freeze({ [STAFF]: 0, [EQUIPMENT]: 1, [GROUP]: 2 })

const primaryColor = getCssVar('primary')
const warningCssColor = getCssVar(warningColor)

const dateTimeFormatter = computed(() => new Intl.DateTimeFormat(locale.value, {
  weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
}))
const formatDateTime = (value) => dateTimeFormatter.value.format(dayjs(value).toDate())
const formatRange = ({ start, end }) => `${formatDateTime(start)} → ${formatDateTime(end)}`

// --- Scale ---
const containerRef = ref(null)
const scrollRef = ref(null)
const taskListCompact = toRef(webSiteStore, 'projectTimelineTaskListCompact')
const expandedTaskListWidth = toRef(webSiteStore, 'projectTimelineTaskListWidth')
const taskListWidth = computed({
  get: () => (taskListCompact.value ? COMPACT_TASK_LIST_WIDTH : expandedTaskListWidth.value),
  set: (width) => { expandedTaskListWidth.value = width },
})
const projectsCollapsed = toRef(webSiteStore, 'projectTimelineProjectsCollapsed')

const {
  zoomIndex,
  zoomLevel,
  rangeStart,
  rangeDays,
  dayWidth,
  trackWidth,
  canZoomIn,
  canZoomOut,
  dayPixels,
  taskListPixels,
  activePreset,
  presetOptions,
  dayIndexOf,
  dateAtClientX,
  initRange,
  scrollToDate,
  scrollToToday,
  extendRangeIfNeededAsync,
  setZoomAsync,
  onWheel,
  resizeTaskList,
  gridStyle,
  todayLeft,
  todayBandStyle,
} = useTimelineViewport({
  scrollRef,
  taskListWidth,
  zoomIndex: toRef(webSiteStore, 'projectTimelineZoomIndex'),
  taskListBounds: TASK_LIST_BOUNDS,
  onExtend: () => loadRangeAsync(),
})

const dateAtTrackX = (event) => dateAtClientX(event.clientX, event.currentTarget)

const initialFocusDate = () => (props.initialDate ? dayjs(props.initialDate) : dayjs())

const knownProjectIds = new Set()

const loadRangeAsync = async () => {
  await data.loadAsync(rangeStart.value, rangeStart.value.add(rangeDays.value, 'day'), props.projectIds)
  const newProjectIds = new Set()
  for (const project of data.state.projects) {
    if (knownProjectIds.has(project.id)) continue
    knownProjectIds.add(project.id)
    newProjectIds.add(project.id)
    if (!projectsCollapsed.value) keepExpanded(projectRootKey(project.id))
  }
  searchMatchedRows.value.filter(row => newProjectIds.has(row.project_id)).forEach(expandRowAncestors)
}

const onScroll = () => {
  if (pan.isPanning() || drag.value) return
  extendRangeIfNeededAsync()
}

// --- Data ---
const projectColor = (project) => project.color || PROJECT_FALLBACK_COLOR
const serviceColor = (project) => project.color || primaryColor

const groupBy = (items, keyOf) => {
  const map = new Map()
  for (const item of items) {
    const key = keyOf(item)
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(item)
  }
  return map
}

const projectRootKey = (projectId) => `p-${projectId}`

const rowsByParent = computed(() => groupBy(
  [...data.state.rows].sort((a, b) =>
    SERVICE_TYPE_ORDER[a.service_type] - SERVICE_TYPE_ORDER[b.service_type] || a.row_number - b.row_number),
  row => row.parent_row_id ?? projectRootKey(row.project_id),
))
const rowsById = computed(() => new Map(data.state.rows.map(row => [row.id, row])))
const childRows = (parentKey) => rowsByParent.value.get(parentKey) ?? []

const allocations = computed(() => data.state.events.filter(ev => ev.type === ALLOCATION))
const allocationsByRow = computed(() => groupBy(allocations.value, ev => ev.budget_document_row_id))
const allocationsByService = computed(() => groupBy(allocations.value, ev => ev.service_id))
const absencesByService = computed(() => groupBy(
  data.state.events.filter(ev => ev.type === ABSENCE),
  ev => ev.service_id,
))

const extentOf = (ranges) => {
  if (!ranges.length) return null
  const start = ranges.reduce((min, r) => (dayjs(r.start).isBefore(min) ? r.start : min), ranges[0].start)
  const end = ranges.reduce((max, r) => (dayjs(r.end).isAfter(max) ? r.end : max), ranges[0].end)
  return { start, end }
}

const rowSummaries = computed(() => {
  const summaries = new Map()
  const summarize = (row) => {
    const children = childRows(row.id).map(summarize)
    const ownExtent = row.start ? [{ start: row.start, end: row.end }] : []
    const summary = {
      events: [...(allocationsByRow.value.get(row.id) ?? []), ...children.flatMap(c => c.events)],
      extent: extentOf([...ownExtent, ...children.map(c => c.extent).filter(Boolean)]),
      confirmed: children.length ? children.every(c => c.confirmed) : row.confirmed,
      isGroup: children.length > 0,
    }
    summaries.set(row.id, summary)
    return summary
  }
  data.state.projects.forEach(p => childRows(projectRootKey(p.id)).forEach(summarize))
  return summaries
})

const projectSummaries = computed(() => new Map(data.state.projects.map(project => {
  const rows = childRows(projectRootKey(project.id)).map(row => rowSummaries.value.get(row.id))
  const confirmedCount = rows.filter(r => r.confirmed).length
  return [project.id, {
    extent: extentOf([project, ...rows.map(r => r.extent)].filter(range => range?.start)),
    rowCount: rows.length,
    confirmedCount,
    confirmedPercent: rows.length ? Math.round((confirmedCount / rows.length) * PERCENT) : 0,
    confirmed: rows.length > 0 && confirmedCount === rows.length,
  }]
})))

// --- Conflicts: an allocation overlapping an absence of the same service ---
const overlaps = (a, b) => dayjs(a.start).isBefore(b.end) && dayjs(b.start).isBefore(a.end)
const laterOf = (a, b) => (dayjs(a).isAfter(b) ? a : b)
const earlierOf = (a, b) => (dayjs(a).isBefore(b) ? a : b)

const scopedProjects = computed(() => (props.projectIds
  ? data.state.projects.filter(project => props.projectIds.includes(project.id))
  : data.state.projects))

const conflicts = computed(() => {
  const projectsById = new Map(data.state.projects.map(project => [project.id, project]))
  const result = []
  absencesByService.value.forEach((absences, serviceId) => {
    const serviceAllocations = allocationsByService.value.get(serviceId) ?? []
    for (const absence of absences) {
      for (const allocation of serviceAllocations.filter(a => overlaps(a, absence))) {
        const key = `${allocation.id}-${absence.id}`
        const row = rowsById.value.get(allocation.budget_document_row_id)
        const project = projectsById.get(allocation.project_id)
        result.push({
          key,
          allocation,
          absence,
          row,
          project,
          start: laterOf(allocation.start, absence.start),
          end: earlierOf(allocation.end, absence.end),
          serviceTitle: rowFullTitle(row),
          projectName: project.name,
          color: serviceColor(project),
        })
      }
    }
  })
  const scopedIds = new Set(scopedProjects.value.map(project => project.id))
  return result
    .filter(conflict => scopedIds.has(conflict.project.id))
    .sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf())
})


const conflictsByEvent = computed(() => {
  const map = new Map()
  for (const conflict of conflicts.value) {
    for (const id of [conflict.allocation.id, conflict.absence.id]) {
      if (!map.has(id)) map.set(id, [])
      map.get(id).push(conflict)
    }
  }
  return map
})
const conflictsByRow = computed(() => groupBy(conflicts.value, c => c.allocation.budget_document_row_id))
const conflictsByProject = computed(() => groupBy(conflicts.value, c => c.project.id))
const conflictGroups = computed(() => sortedProjects.value
  .filter(project => conflictsByProject.value.has(project.id))
  .map(project => ({ project, conflicts: conflictsByProject.value.get(project.id) })))

const conflictsOf = (entry) => {
  if (entry.kind === PROJECT) return conflictsByProject.value.get(entry.project.id) ?? []
  if (entry.kind === EVENTS) return conflictsByRow.value.get(entry.row.id) ?? []
  if (entry.kind === ABSENCES) return conflicts.value.filter(c => c.absence.service_id === entry.row.service_id)
  const allocationIds = new Set(entry.summary.events.map(ev => ev.id))
  return conflicts.value.filter(c => allocationIds.has(c.allocation.id))
}

const withConflicts = (entry) => {
  const entryConflicts = conflictsOf(entry)
  return {
    ...entry,
    conflicts: entryConflicts,
    bars: entry.bars.map(bar => ({
      ...bar,
      conflict: bar.event ? conflictsByEvent.value.has(bar.event.id) : entryConflicts.length > 0,
    })),
  }
}

const sortedProjects = computed(() =>
  [...scopedProjects.value].sort((a, b) => dayjs(b.start).valueOf() - dayjs(a.start).valueOf()),
)

const projectSearch = ref('')
const searchQuery = computed(() => projectSearch.value?.toLowerCase().trim() ?? '')
const matchesProjectSearch = (project) => [project.name, project.listing_name].some(text => includesQuery(text, searchQuery.value))
const searchMatchedRows = computed(() => (searchQuery.value ? data.state.rows.filter(row => matchesServiceSearch(row, searchQuery.value)) : []))
const searchMatchedRowIds = computed(() => new Set(searchMatchedRows.value.map(row => row.id)))
const searchedProjects = computed(() => {
  if (!searchQuery.value) return sortedProjects.value
  const projectIdsWithMatchedRows = new Set(searchMatchedRows.value.map(row => row.project_id))
  return sortedProjects.value.filter(project => projectIdsWithMatchedRows.has(project.id) || matchesProjectSearch(project))
})

let expandedBeforeSearch = null

const keepExpanded = (key) => {
  expanded.value.add(key)
  expandedBeforeSearch?.add(key)
}

const onProjectSearch = (value) => {
  projectSearch.value = value
  if (!searchQuery.value) {
    if (expandedBeforeSearch) expanded.value = expandedBeforeSearch
    expandedBeforeSearch = null
    return
  }
  expandedBeforeSearch ??= new Set(expanded.value)
  expanded.value = new Set(expandedBeforeSearch)
  searchMatchedRows.value.forEach(expandRowAncestors)
}

const hasListingText = (row) => row.listing_item_text && row.listing_item_text !== row.service_name
const rowTitle = (row) => (hasListingText(row) ? row.listing_item_text : row.service_title)
const rowFullTitle = (row) => (hasListingText(row) ? `${row.listing_item_text} - ${row.service_title}` : row.service_title)
const serviceIcon = (vr) => SERVICE_TYPE_ICONS[vr.summary.isGroup ? GROUP : vr.row.service_type]
const unloadableAvatars = reactive(new Set())

const isPlannedShifted = (row, extent) =>
  extent && (!dayjs(row.planned_start).isSame(extent.start) || !dayjs(row.planned_end).isSame(extent.end))

const projectBars = (project, summary) => [{
  key: `pb-${project.id}`,
  ...summary.extent,
  dayLevel: true,
  isProject: true,
  color: projectColor(project),
  label: project.name,
  tooltip: project.name,
}]

const serviceBars = (project, row, summary) => {
  if (!summary.extent) return []
  return [{
    key: `rb-${row.id}`,
    ...summary.extent,
    dayLevel: true,
    color: serviceColor(project),
    label: rowTitle(row),
    tooltip: rowFullTitle(row),
    unconfirmed: !summary.confirmed,
    icon: summary.isGroup ? SERVICE_TYPE_ICONS[GROUP] : null,
    availabilityColor: !summary.confirmed && row.insufficient_availability ? warningCssColor : null,
    availabilityLabel: !summary.confirmed && row.insufficient_availability ? t('common.insufficientAvailability') : null,
  }]
}

const timeRangeLabel = (ev) => `${dayjs(ev.start).format('HH:mm')} – ${dayjs(ev.end).format('HH:mm')}`

const allocationBars = (events, project, row) => events.map(ev => ({
  key: `ev-${ev.id}`,
  start: ev.start,
  end: ev.end,
  color: serviceColor(project),
  label: timeRangeLabel(ev),
  tooltip: rowFullTitle(row),
  unconfirmed: !ev.confirmed,
  event: ev,
}))

const absenceBars = (absences, row) => absences.map(ev => ({
  key: `ev-${ev.id}`,
  start: ev.start,
  end: ev.end,
  color: ABSENCE_COLOR,
  label: t('common.staff.absence'),
  tooltip: row.service_title,
  icon: ABSENCE_ICON,
  unconfirmed: !ev.confirmed,
  event: ev,
}))

const visibleRows = computed(() => {
  const result = []
  const pushRow = (project, row, depth) => {
    const key = `r-${row.id}`
    const summary = rowSummaries.value.get(row.id)
    result.push(withConflicts({ key, kind: SERVICE, project, row, depth, summary, bars: serviceBars(project, row, summary) }))
    if (!expanded.value.has(key)) return
    if (summary.isGroup) {
      childRows(row.id).forEach(child => pushRow(project, child, depth + 1))
      return
    }
    const events = allocationsByRow.value.get(row.id) ?? []
    result.push(withConflicts({ key: `e-${row.id}`, kind: EVENTS, project, row, depth, count: events.length, bars: allocationBars(events, project, row) }))
    if (row.service_type !== STAFF) return
    const absences = absencesByService.value.get(row.service_id) ?? []
    const projectExtent = projectSummaries.value.get(project.id).extent
    const absencesInProject = absences.filter(absence => overlaps(absence, projectExtent)).length
    result.push(withConflicts({ key: `a-${row.id}`, kind: ABSENCES, project, row, depth, count: absencesInProject, bars: absenceBars(absences, row) }))
  }
  for (const project of searchedProjects.value) {
    const key = projectRootKey(project.id)
    const summary = projectSummaries.value.get(project.id)
    result.push(withConflicts({ key, kind: PROJECT, project, summary, bars: projectBars(project, summary) }))
    if (expanded.value.has(key)) childRows(key).forEach(row => pushRow(project, row, 0))
  }
  return result
})

const plannedBox = (vr) => {
  if (vr.kind !== SERVICE || vr.summary.isGroup || !isPlannedShifted(vr.row, vr.summary.extent)) return null
  return { start: vr.row.planned_start, end: vr.row.planned_end }
}

// --- Expand / select ---
const expanded = ref(new Set())
const selectedRowKey = ref(null)
const selectedBar = ref(null)

const toggleExpanded = (key) => {
  if (expanded.value.has(key)) expanded.value.delete(key)
  else expanded.value.add(key)
}

const expandRowAncestors = (row) => {
  expanded.value.add(projectRootKey(row.project_id))
  for (let parent = rowsById.value.get(row.parent_row_id); parent; parent = rowsById.value.get(parent.parent_row_id)) {
    expanded.value.add(`r-${parent.id}`)
  }
}

const expandAll = () => {
  projectsCollapsed.value = false
  expandedBeforeSearch = null
  sortedProjects.value.forEach(p => expanded.value.add(projectRootKey(p.id)))
  rowSummaries.value.forEach((summary, rowId) => { if (summary.isGroup) expanded.value.add(`r-${rowId}`) })
}

const collapseAll = () => {
  projectsCollapsed.value = true
  expandedBeforeSearch = null
  expanded.value.clear()
}

const hasExpandedRows = computed(() => expanded.value.size > 0)
const toggleExpandAll = () => (hasExpandedRows.value ? collapseAll() : expandAll())

let selectedProjectId = null

const selectProject = (project) => {
  if (project.id === selectedProjectId) return
  selectedProjectId = project.id
  emit('select-project-listing', project.listing_id ?? null)
}

const selectRow = (vr) => {
  selectedRowKey.value = vr.key
  selectedBar.value = null
  selectProject(vr.project)
}

// --- Geometry ---
const drag = ref(null)

const isDragged = (bar) => drag.value?.bar.key === bar.key && drag.value.moved
const barRange = (bar) => (isDragged(bar) ? drag.value.preview : bar)

const spanStyle = ({ start, end }) => {
  const left = dayIndexOf(start) * dayWidth.value
  const width = Math.max((dayIndexOf(end) - dayIndexOf(start)) * dayWidth.value, MIN_BAR_WIDTH)
  return { left: `${left}rem`, width: `${width}rem` }
}

const barStyle = (bar) => ({ ...spanStyle(barRange(bar)), '--bar-color': bar.color })

const barClasses = (bar) => ({
  'project-timeline__bar': true,
  'project-timeline__bar--project': bar.isProject,
  'project-timeline__bar--unconfirmed': bar.unconfirmed,
  'project-timeline__bar--conflict': bar.conflict,
  'project-timeline__bar--selected': selectedBar.value?.key === bar.key,
  'project-timeline__bar--dragging': isDragged(bar),
  'project-timeline__bar--focused': focusedBarKey.value === bar.key,
})

const rowClasses = (vr) => ({
  'project-timeline__row': true,
  'project-timeline__row--project': vr.kind === PROJECT,
  'project-timeline__row--service': vr.kind === SERVICE,
  'project-timeline__row--lane': vr.kind === EVENTS || vr.kind === ABSENCES,
  'project-timeline__row--search-match': vr.kind === SERVICE && searchMatchedRowIds.value.has(vr.row.id),
  'project-timeline__row--selected': selectedRowKey.value === vr.key,
  'project-timeline__row--drop': dropProjectId.value === vr.project.id,
  'project-timeline__row--conflict': vr.conflicts.length > 0,
})

const dragLabel = (vr) => (drag.value?.moved && drag.value.vr.key === vr.key ? drag.value.preview : null)

// --- Pan ---
const pan = useTimelinePan(containerRef)
let panStartLeft = 0

const onPanStart = (event) => {
  if (event.button !== 0 || event.pointerType === 'touch') return
  panStartLeft = scrollRef.value.scrollLeft
  pan.beginElement(event, scrollRef.value, scrollRef.value)
  addWindowListeners()
}

// --- Bar drag / resize ---
const shiftedBy = (iso, bar, deltaDays) => {
  if (bar.dayLevel) return dayjs(iso).add(Math.round(deltaDays), 'day')
  const snap = zoomLevel.value.snapMinutes
  return dayjs(iso).add(Math.round((deltaDays * MINUTES_PER_DAY) / snap) * snap, 'minute')
}

const previewFor = ({ bar, mode }, deltaDays) => {
  const start = dayjs(bar.start)
  const end = dayjs(bar.end)
  if (mode === MOVE) return { start: shiftedBy(bar.start, bar, deltaDays), end: shiftedBy(bar.end, bar, deltaDays) }
  if (mode === RESIZE_START) {
    const next = shiftedBy(bar.start, bar, deltaDays)
    const valid = bar.dayLevel ? !next.isAfter(end, 'day') : next.isBefore(end)
    return valid ? { start: next, end } : null
  }
  const next = shiftedBy(bar.end, bar, deltaDays)
  const valid = bar.dayLevel ? !next.isBefore(start, 'day') : next.isAfter(start)
  return valid ? { start, end: next } : null
}

const selectBar = (vr, bar) => {
  selectedRowKey.value = vr.key
  selectedBar.value = bar
  selectProject(vr.project)
}

// On touch a swipe starting on a bar must scroll the timeline: only a bar selected by a tap is dragged.
const onBarPointerDown = (event, vr, bar, mode) => {
  if (event.button !== 0) return
  if (event.pointerType === 'touch' && selectedBar.value?.key !== bar.key) return
  event.stopPropagation()
  selectBar(vr, bar)
  drag.value = { vr, bar, mode, originX: event.clientX, moved: false, preview: { start: bar.start, end: bar.end } }
  addWindowListeners()
}

// --- Project dates: reschedule the project, or keep a service/event within its bounds ---
const rescheduleDialogs = ref(null)
const projectBounds = ref(null)

const projectHasAllocations = (projectId) => data.state.rows.some(row => row.project_id === projectId)

const rescheduleProjectAsync = async (project, start, end) => {
  const decision = await rescheduleDialogs.value.confirm({
    project,
    hasAllocations: projectHasAllocations(project.id),
    datesChanged: true,
    slotsChanged: false,
  })
  if (decision.blocked || decision.cancelled) return
  await data.updateProjectAsync(project, {
    start: dayjs(start).toISOString(),
    end: dayjs(end).toISOString(),
    ...(decision.action != null && { allocation_action: decision.action }),
  })
}

// A range lying entirely outside the bounds collapses onto the boundary day it overshot.
const clampToProject = ({ start, end }, project) => {
  const boundsStart = dayjs(project.start)
  const boundsEnd = dayjs(project.end)
  if (dayjs(start).isAfter(boundsEnd)) return { start: boundsEnd.startOf('day').toISOString(), end: boundsEnd.toISOString() }
  if (dayjs(end).isBefore(boundsStart)) return { start: boundsStart.toISOString(), end: boundsStart.endOf('day').toISOString() }
  return {
    start: (dayjs(start).isBefore(boundsStart) ? boundsStart : dayjs(start)).toISOString(),
    end: (dayjs(end).isAfter(boundsEnd) ? boundsEnd : dayjs(end)).toISOString(),
  }
}

const applyWithinProjectBounds = (project, range, apply) => {
  if (!dayjs(range.start).isBefore(project.start) && !dayjs(range.end).isAfter(project.end)) {
    apply(range)
    return
  }
  projectBounds.value = { project, requested: range, trimmed: clampToProject(range, project), apply }
}

const projectBoundsOptions = computed(() => [
  { key: projectBoundsActionEnum.EXTEND, label: t('common.extendProjectDates'), icon: 'mdi-calendar-expand-horizontal', color: 'primary', standalone: true },
  { key: projectBoundsActionEnum.TRIM, label: t('common.alignToProjectDates'), icon: 'mdi-arrow-collapse-horizontal', color: 'primary', standalone: true },
])

// EXTEND applies the requested range as is: the backend widens the project to the days it spans.
const onProjectBoundsSelect = (key) => {
  const { requested, trimmed, apply } = projectBounds.value
  projectBounds.value = null
  apply(key === projectBoundsActionEnum.TRIM ? trimmed : requested)
}

const wholeDays = ({ start, end }) => dayjs(end).startOf('day').diff(dayjs(start).startOf('day'), 'day')

const applyServiceRange = (vr, bar, mode, range) =>
  data.changeRowRangeAsync(vr.row, range, mode === MOVE && wholeDays(range) === wholeDays(bar))

const projectOf = (event) => data.state.projects.find(project => project.id === event.project_id)

const saveAllocation = (event) => applyWithinProjectBounds(projectOf(event), event, range => data.saveEventAsync({ ...event, ...range }))

const commitDrag = ({ vr, bar, mode, preview }) => {
  if (vr.kind === PROJECT) {
    rescheduleProjectAsync(vr.project, preview.start, preview.end)
    return
  }
  if (vr.kind === SERVICE) {
    applyWithinProjectBounds(vr.project, preview, range => applyServiceRange(vr, bar, mode, range))
    return
  }
  if (bar.event.type === ALLOCATION) {
    saveAllocation({ ...bar.event, ...preview })
    return
  }
  data.saveEventAsync({ ...bar.event, ...preview })
}

// --- Task list resize ---
let taskListResize = null

const onResizerPointerDown = (event) => {
  if (event.button !== 0) return
  event.stopPropagation()
  taskListResize = { originX: event.clientX, width: taskListWidth.value }
  addWindowListeners()
}

// --- Window pointer tracking (pan, bar drag, task list resize) ---
const onWindowPointerMove = (event) => {
  if (pan.move(event)) return
  if (taskListResize) {
    resizeTaskList(taskListResize, event.clientX)
    return
  }
  const current = drag.value
  if (!current) return
  const deltaX = event.clientX - current.originX
  if (!current.moved && Math.abs(deltaX) < DRAG_THRESHOLD) return
  const preview = previewFor(current, deltaX / dayPixels())
  if (!preview) return
  current.moved = true
  current.preview = { start: preview.start.toISOString(), end: preview.end.toISOString() }
}

const stopTracking = () => {
  window.removeEventListener('pointermove', onWindowPointerMove)
  window.removeEventListener('pointerup', onWindowPointerUp)
  taskListResize = null
  drag.value = null
}

const onWindowPointerUp = () => {
  const current = drag.value
  const panned = pan.isPanning() && scrollRef.value?.scrollLeft !== panStartLeft
  pan.end()
  stopTracking()
  if (current?.moved) commitDrag(current)
  if (panned) extendRangeIfNeededAsync()
}

const addWindowListeners = () => {
  window.addEventListener('pointermove', onWindowPointerMove)
  window.addEventListener('pointerup', onWindowPointerUp)
}

// --- Event / absence editing ---
const editing = ref(null)

const canApproveAbsences = computed(() => webSiteStore.can(webSiteStore.permissionFlagEnum.APPROVALS))

const editingTitle = computed(() => (editing.value?.event.type === ABSENCE ? t('common.staff.absence') : t('common.event', 1)))
const editingIcon = computed(() => (editing.value?.event.type === ABSENCE ? ABSENCE_ICON : 'mdi-calendar-clock'))

const atTime = (day, time) => dayjs(`${day.format(DATE_FORMAT)}T${time}`).toISOString()

const openNewEvent = (vr, day, isAbsence = vr.kind === ABSENCES) => {
  editing.value = {
    event: {
      id: null,
      type: isAbsence ? ABSENCE : ALLOCATION,
      project_id: isAbsence ? null : vr.project.id,
      service_id: vr.row.service_id,
      budget_document_row_id: isAbsence ? null : vr.row.id,
      start: atTime(day, webSiteStore.getDefaultStartTime()),
      end: atTime(day, webSiteStore.getDefaultEndTime()),
      confirmed: false,
    },
  }
}

const onLaneAdd = (vr) => {
  const extent = rowSummaries.value.get(vr.row.id)?.extent
  openNewEvent(vr, dayjs(extent?.start).startOf('day'))
}

const onTrackDblClick = (event, vr) => {
  if (vr.kind !== EVENTS && vr.kind !== ABSENCES) return
  openNewEvent(vr, dateAtTrackX(event))
}

const onBarDblClick = (vr, bar) => {
  if (bar.event) {
    editing.value = { event: bar.event }
    return
  }
  toggleExpanded(vr.key)
}

const onEventSave = (event) => {
  editing.value = null
  if (event.type === ALLOCATION) {
    saveAllocation(event)
    return
  }
  data.saveEventAsync(event)
}

// --- Conflict resolution ---
const conflictToResolve = ref(null)

const openConflict = (conflict) => {
  conflictToResolve.value = conflict
}

const DELETING_RESOLUTIONS = Object.freeze({
  [DELETE_ALLOCATION]: conflict => conflict.allocation,
  [DELETE_ABSENCE]: conflict => conflict.absence,
})

const onConflictResolve = (conflict, resolution) => {
  conflictToResolve.value = null
  const deleted = DELETING_RESOLUTIONS[resolution]?.(conflict)
  if (deleted) {
    askDeleteConfirmation(eventLabel(deleted), () => data.deleteEventAsync(deleted))
    return
  }
  if (resolution === SHIFT_ALLOCATION) {
    saveAllocation(data.allocationAfterAbsence(conflict.allocation, conflict.absence))
    return
  }
  data.trimAbsenceAsync(conflict.absence, conflict.allocation)
}

const focusAllocationAsync = async (row, range, barKey) => {
  expandRowAncestors(row)
  expanded.value.add(`r-${row.id}`)
  await nextTick()
  scrollRef.value?.querySelector(`[data-row-key="e-${row.id}"]`)?.scrollIntoView({ block: 'center' })
  focusRange(range, barKey)
}

const goToConflictAsync = (conflict) => focusAllocationAsync(conflict.row, conflict, `ev-${conflict.allocation.id}`)

// The allocation of `serviceId` (any service when null) in `projectId` closest to `date`.
const focusServiceAllocationAsync = ({ projectId, serviceId, date }) => {
  const rows = data.state.rows.filter(row => row.project_id === projectId && (serviceId == null || row.service_id === serviceId))
  const distance = (event) => (overlaps(event, { start: dayjs(date).startOf('day'), end: dayjs(date).endOf('day') })
    ? 0
    : Math.abs(dayjs(event.start).diff(date)))
  const [closest] = rows
    .flatMap(row => (allocationsByRow.value.get(row.id) ?? []).map(event => ({ row, event })))
    .sort((a, b) => distance(a.event) - distance(b.event))
  if (closest) return focusAllocationAsync(closest.row, closest.event, `ev-${closest.event.id}`)
  if (rows.length) return focusAllocationAsync(rows[0], { start: date, end: date }, null)
  scrollToDate(date, CENTER_VIEWPORT_RATIO)
}

// --- Delete confirmation ---
const pendingDelete = ref(null)

const eventLabel = (event) =>
  `${event.type === ABSENCE ? t('common.staff.absence') : t('common.event', 1)} (${formatRange(event)})`

const askDeleteConfirmation = (item, action) => {
  pendingDelete.value = { message: t('common.confirmDeleteMessage', { item }), action }
}

const onDeleteConfirmed = () => {
  pendingDelete.value?.action()
  pendingDelete.value = null
}

const onEventDelete = (event) => {
  editing.value = null
  askDeleteConfirmation(eventLabel(event), () => {
    selectedBar.value = null
    return data.deleteEventAsync(event)
  })
}

// --- Allocation confirmation ---
const allocationToConfirm = ref(null)

const confirmRow = ({ row, project, summary }) => {
  const service = planningStore.getServiceById(row.service_id)
  if (summary.isGroup || !service || planningStore.isUnitaryService(service)) {
    confirmAllocationAsync(summary.isGroup ? null : row.service_id, project.id, row.id, data.reloadAsync)
    return
  }
  allocationToConfirm.value = {
    serviceId: row.service_id,
    projectId: project.id,
    budgetDocumentRowId: row.id,
    serviceName: rowFullTitle(row),
    quantity: Number(row.quantity),
    projectStart: project.start,
  }
}

const onAllocationConfirmed = () => {
  allocationToConfirm.value = null
  data.reloadAsync()
}

const confirmProject = (project) => confirmAllocationAsync(null, project.id, null, data.reloadAsync)

// --- Focus ---
const focusedBarKey = ref(null)
let focusTimer = null

const focusRange = (range, barKey) => {
  const el = scrollRef.value
  if (!el) return
  const viewport = el.clientWidth - taskListPixels()
  const startX = dayIndexOf(range.start) * dayPixels()
  const width = (dayIndexOf(range.end) - dayIndexOf(range.start)) * dayPixels()
  el.scrollLeft = width <= viewport
    ? startX + width * CENTER_VIEWPORT_RATIO - viewport * CENTER_VIEWPORT_RATIO
    : startX - viewport * FOCUS_START_MARGIN_RATIO
  clearTimeout(focusTimer)
  focusedBarKey.value = barKey
  focusTimer = setTimeout(() => { focusedBarKey.value = null }, HIGHLIGHT_DURATION_MS)
  extendRangeIfNeededAsync()
}

// --- Context menu ---
const contextTarget = ref(null)

const contextTargetAt = ({ target, clientX }, vr) => {
  const barKey = target.closest('.project-timeline__bar')?.dataset.barKey
  const track = target.closest('.project-timeline__track')
  const bar = vr.bars.find(b => b.key === barKey) ?? null
  return { vr, bar, day: track ? dateAtClientX(clientX, track) : null }
}

const onRowContextMenu = (event, vr) => {
  contextTarget.value = contextTargetAt(event, vr)
  selectBar(vr, contextTarget.value.bar)
}

// On mobile Quasar opens the context menu on a touch hold, without a `contextmenu` event.
const onRowTouchStart = (event, vr) => {
  contextTarget.value = contextTargetAt(event.touches[0], vr)
}

const contextTitle = computed(() => {
  const target = contextTarget.value
  if (!target) return null
  if (target.bar) return target.bar.tooltip
  return target.vr.kind === PROJECT ? target.vr.project.name : rowFullTitle(target.vr.row)
})

const centerAction = (range, barKey) => ({
  key: 'center',
  icon: 'mdi-crosshairs-gps',
  label: t('common.centerInTimeline'),
  action: () => focusRange(range, barKey),
})

const resolveAction = (conflict) => ({
  key: 'resolve',
  icon: 'mdi-alert',
  color: warningColor,
  label: t('common.resolveConflict'),
  action: () => openConflict(conflict),
})

const expandAction = (vr) => {
  const isOpen = expanded.value.has(vr.key)
  return {
    key: 'expand',
    icon: isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down',
    label: t(isOpen ? 'common.collapse' : 'common.expand'),
    action: () => toggleExpanded(vr.key),
  }
}

const addEventAction = (vr, day, isAbsence) => ({
  key: isAbsence ? 'addAbsence' : 'addEvent',
  icon: isAbsence ? ABSENCE_ICON : 'mdi-calendar-plus',
  label: t(isAbsence ? 'common.addAbsence' : 'common.addEvent'),
  action: () => openNewEvent(vr, day, isAbsence),
})

const deleteAction = (label, action) => ({ key: 'delete', icon: 'mdi-delete-outline', color: 'negative', label, action })

const availabilityAction = (row, day) => ({
  key: 'availability',
  icon: 'mdi-warehouse',
  label: t('warehouseAvailability.checkAvailability'),
  action: () => router.push({
    path: menuRoutes[menuRouteEnum.WAREHOUSE_STOCK],
    query: { serviceId: row.service_id, date: dayjs(day).format('YYYY-MM-DD') },
  }),
})

const askAssistantAction = (name) => chatStore.isEnabled && {
  key: 'askAssistant',
  icon: 'mdi-creation',
  label: t('chat.askAssistant'),
  action: () => chatStore.askInNewConversation(t('chat.askAssistantAbout', { name })),
}

const eventBarActions = (vr, bar, conflict) => {
  const { event } = bar
  const edit = { key: 'edit', icon: 'mdi-pencil-outline', label: t('common.edit'), action: () => { editing.value = { event } } }
  const primary = [conflict && resolveAction(conflict)]
  if (event.type === ABSENCE) {
    if (canApproveAbsences.value) {
      primary.push(event.confirmed
        ? { key: 'revert', icon: 'mdi-undo-variant', label: t('common.restore'), action: () => data.setAbsenceApprovalAsync(event, false) }
        : { key: 'approve', icon: 'mdi-check', color: 'positive', label: t('common.approve'), action: () => data.setAbsenceApprovalAsync(event, true) })
    }
    primary.push(edit)
  } else {
    primary.push(edit, replaceAction(vr))
  }
  return {
    primary,
    secondary: [centerAction(bar, bar.key), availabilityAction(vr.row, event.start)],
    danger: [deleteAction(t('common.delete'), () => onEventDelete(event))],
  }
}

const laneActions = (vr, day, range) => ({
  primary: [addEventAction(vr, day ?? dayjs(range?.start).startOf('day'), vr.kind === ABSENCES)],
  secondary: [range && centerAction(range, null), availabilityAction(vr.row, day ?? range?.start)],
  danger: [],
})

// Sections of the context menu for the clicked element: frequent actions, the others, deletions.
const targetActions = ({ vr, bar, day }) => {
  const range = bar ?? extentOf(vr.bars)
  const conflict = bar?.event ? conflictsByEvent.value.get(bar.event.id)?.[0] : vr.conflicts[0]
  if (bar?.event) return eventBarActions(vr, bar, conflict)
  if (vr.kind === EVENTS || vr.kind === ABSENCES) return laneActions(vr, day, range)
  const center = range && centerAction(range, vr.bars.length === 1 ? vr.bars[0].key : null)
  return vr.kind === PROJECT
    ? projectActions(vr, conflict, center)
    : serviceActions(vr, conflict, center, day ?? dayjs(range?.start).startOf('day'))
}

// --- Service replacement ---
const replacing = ref(null)

const replaceAction = ({ row, project }) => ({
  key: 'replace',
  icon: 'mdi-swap-horizontal',
  label: t('common.replaceService'),
  action: () => {
    replacing.value = { row, project, title: rowFullTitle(row), confirmed: rowSummaries.value.get(row.id)?.confirmed ?? false }
  },
})

const onReplace = async (serviceId) => {
  const { row } = replacing.value
  replacing.value = null
  const rowId = await data.replaceServiceAsync(row, serviceId)
  await highlightRowAsync(`r-${rowId}`, scrollRef)
}

const serviceActions = (vr, conflict, center, day) => {
  const { row, project, summary } = vr
  const hasAllocation = Boolean(summary.extent)
  const confirmToggle = summary.confirmed
    ? { key: 'cancel', icon: 'mdi-close-circle-outline', label: t('common.cancelAllocation'), action: () => data.cancelAllocationAsync(summary.isGroup ? null : row.service_id, project.id, row.id) }
    : { key: 'confirm', icon: 'mdi-check-circle-outline', color: 'positive', label: t('common.confirmAllocation'), action: () => confirmRow(vr) }
  const isLeaf = !summary.isGroup
  return {
    primary: [conflict && resolveAction(conflict), hasAllocation && confirmToggle, hasAllocation && replaceAction(vr)],
    secondary: [
      isLeaf && addEventAction(vr, day, false),
      isLeaf && row.service_type === STAFF && addEventAction(vr, day, true),
      expandAction(vr),
      center,
      isLeaf && availabilityAction(row, day),
    ],
    danger: [hasAllocation && deleteAction(t('common.delete'), () => askDeleteConfirmation(rowFullTitle(row), () => data.deleteRowAsync(row)))],
  }
}

const projectActions = (vr, conflict, center) => {
  const { project, summary } = vr
  const hasRows = summary.rowCount > 0
  return {
    primary: [
      conflict && resolveAction(conflict),
      hasRows && !summary.confirmed && { key: 'confirmAll', icon: 'mdi-check-circle-outline', color: 'positive', label: t('common.confirmAllAllocations'), action: () => confirmProject(project) },
      hasRows && summary.confirmedCount > 0 && { key: 'cancelAll', icon: 'mdi-close-circle-outline', label: t('common.cancelAllAllocations'), action: () => data.cancelAllocationAsync(null, project.id) },
    ],
    secondary: [
      expandAction(vr),
      center,
      {
        key: 'lock',
        icon: project.lock_planned ? 'mdi-lock-open-variant-outline' : 'mdi-lock-outline',
        label: t(project.lock_planned ? 'common.unlockPlanned' : 'common.lockPlanned'),
        action: () => data.updateProjectAsync(project, { lock_planned: !project.lock_planned }),
      },
    ],
    danger: [hasRows && deleteAction(
      t('common.deleteAllocations'),
      () => askDeleteConfirmation(`${t('common.deleteAllocations').toLowerCase()} - ${project.name}`, () => data.deleteProjectAllocationsAsync(project)),
    )],
  }
}

const contextSections = computed(() => {
  const general = [
    { key: 'today', icon: 'mdi-calendar-today', label: t('common.today'), action: scrollToToday },
    {
      key: 'toggleExpandAll',
      icon: hasExpandedRows.value ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal',
      label: t(hasExpandedRows.value ? 'common.collapseAll' : 'common.expandAll'),
      action: toggleExpandAll,
    },
  ]
  const target = contextTarget.value
  if (!target) return [general]
  const { primary, secondary, danger } = targetActions(target)
  return [primary, [...secondary, askAssistantAction(contextTitle.value)], general, danger]
    .map(section => section.filter(Boolean))
    .filter(section => section.length)
})

useKeydown((event) => {
  if (event.key === 'Escape' && (drag.value || pan.isPanning())) {
    pan.revert()
    stopTracking()
    return
  }
  const isTyping = ['INPUT', 'TEXTAREA'].includes(event.target?.tagName)
  if ((event.key === 'Delete' || event.key === 'Backspace') && !isTyping && !editing.value && !pendingDelete.value && selectedBar.value?.event) {
    onEventDelete(selectedBar.value.event)
  }
})

// --- Drop from the assignment sidebar ---
const dropProjectId = ref(null)

const onRowDragOver = (event, vr) => {
  if (!props.draggedService) return
  event.preventDefault()
  event.dataTransfer.dropEffect = 'copy'
  dropProjectId.value = vr.project.id
}

const onCanvasDragLeave = (event) => {
  if (!event.currentTarget.contains(event.relatedTarget)) dropProjectId.value = null
}

const pendingDuplicate = ref(null)

const isServiceInProject = (projectId, serviceId) =>
  data.state.rows.some(row => row.project_id === projectId && row.service_id === serviceId)

const groupToAssign = ref(null)
const quantityToAssign = ref(null)

const assignAsync = async (project, service, options = {}) => {
  const rowId = await data.assignServiceAsync(project, service, options)
  keepExpanded(projectRootKey(project.id))
  await highlightRowAsync(`r-${rowId}`, scrollRef)
}

const addServiceToProjectAsync = async (project, service) => {
  if (service.type === GROUP && service.components?.length > 1) {
    groupToAssign.value = { project, service }
    return
  }
  const maxQuantity = service.type === GROUP ? null : Math.floor(Number(service.total_quantity ?? 1))
  if (planningStore.isUnitaryService(service) || maxQuantity === 1) {
    await assignAsync(project, service, { quantity: 1 })
    return
  }
  quantityToAssign.value = { project, service, quantity: 1, max: maxQuantity }
}

const onGroupComponentsConfirm = ({ quantity, selectedComponentIds }) => {
  const { project, service } = groupToAssign.value
  groupToAssign.value = null
  assignAsync(project, service, { quantity, selectedComponentIds })
}

const onQuantityConfirm = () => {
  const { project, service, quantity } = quantityToAssign.value
  quantityToAssign.value = null
  assignAsync(project, service, { quantity })
}

const onRowDropAsync = async (event, vr) => {
  const service = props.draggedService
  dropProjectId.value = null
  if (!service) return
  event.preventDefault()
  emit('update:draggedService', null)
  if (isServiceInProject(vr.project.id, service.id)) {
    if (planningStore.isUnitaryService(service)) {
      notifyWarning(t('warning.unitaryServiceAlreadyInProject'))
      return
    }
    pendingDuplicate.value = { project: vr.project, service }
    return
  }
  await addServiceToProjectAsync(vr.project, service)
}

const onDuplicateConfirmed = () => {
  const { project, service } = pendingDuplicate.value
  pendingDuplicate.value = null
  addServiceToProjectAsync(project, service)
}

// --- Lifecycle ---
// The splitter lays the pane out after mount: wait for a real width before sizing the window and loading.
let initialScrollObserver = null

const reload = () => data.refreshAsync()

onMounted(() => {
  initialScrollObserver = new ResizeObserver(async () => {
    if (!scrollRef.value?.clientWidth) return
    initialScrollObserver.disconnect()
    initRange(initialFocusDate(), INITIAL_SPAN_DAYS)
    const initialLoad = loadRangeAsync()
    await nextTick()
    scrollToDate(initialFocusDate(), TODAY_VIEWPORT_RATIO)
    if (props.focus) initialLoad.then(() => focusServiceAllocationAsync(props.focus))
  })
  initialScrollObserver.observe(scrollRef.value)
})

onBeforeUnmount(() => {
  clearTimeout(focusTimer)
  initialScrollObserver?.disconnect()
  stopTracking()
})

defineExpose({ reload })
</script>

<template>
  <div ref="containerRef" :class="['project-timeline', { 'project-timeline--compact': taskListCompact, 'project-timeline--mobile': isMobile }]">
    <div class="project-timeline__toolbar">
      <q-btn-toggle
        :model-value="activePreset"
        :options="presetOptions"
        class="project-timeline__presets"
        no-caps
        unelevated
        dense
        toggle-color="primary"
        text-color="grey-8"
        @update:model-value="value => setZoomAsync(value)"
      />
      <div class="project-timeline__zoom">
        <q-btn
          flat
          round
          dense
          size="sm"
          icon="mdi-minus"
          :disable="!canZoomOut"
          @click="setZoomAsync(zoomIndex - 1)"
        >
          <q-tooltip>{{ $t('common.zoomOut') }}</q-tooltip>
        </q-btn>
        <q-btn
          flat
          round
          dense
          size="sm"
          icon="mdi-plus"
          :disable="!canZoomIn"
          @click="setZoomAsync(zoomIndex + 1)"
        >
          <q-tooltip>{{ $t('common.zoomIn') }}</q-tooltip>
        </q-btn>
      </div>
      <q-btn
        outline
        rounded
        dense
        no-caps
        color="primary"
        icon="mdi-calendar-today"
        :label="isMobile ? undefined : $t('common.today')"
        class="project-timeline__today-btn"
        @click="scrollToToday"
      />
      <q-btn
        flat
        round
        dense
        size="sm"
        color="grey-7"
        :icon="hasExpandedRows ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal'"
        @click="toggleExpandAll"
      >
        <q-tooltip>{{ hasExpandedRows ? $t('common.collapseAll') : $t('common.expandAll') }}</q-tooltip>
      </q-btn>
      <q-btn
        flat
        round
        dense
        size="sm"
        color="grey-7"
        :icon="taskListCompact ? 'mdi-arrow-expand-right' : 'mdi-arrow-collapse-left'"
        @click="taskListCompact = !taskListCompact"
      >
        <q-tooltip>{{ taskListCompact ? $t('common.showRowDescriptions') : $t('common.hideRowDescriptions') }}</q-tooltip>
      </q-btn>

      <q-space />

      <q-btn
        v-if="conflicts.length"
        unelevated
        rounded
        dense
        no-caps
        :color="warningColor"
        icon="mdi-alert"
        :label="`${conflicts.length} ${$t('common.conflict', conflicts.length)}`"
        class="project-timeline__conflicts-btn"
      >
        <q-menu
          anchor="bottom right"
          self="top right"
          :offset="[0, 8]"
          class="project-timeline__conflict-menu"
        >
          <div class="project-timeline__conflict-header">
            <q-avatar
              icon="mdi-alert"
              :color="warningColor"
              text-color="white"
              size="md"
            />
            <div class="col">
              <div class="text-subtitle1 text-weight-medium">
                {{ $t('common.conflict', 2) }}
              </div>
              <div class="text-caption text-grey-7">
                {{ $t('common.conflictsHint') }}
              </div>
            </div>
          </div>
          <q-list class="project-timeline__conflict-list">
            <template v-for="group in conflictGroups" :key="group.project.id">
              <q-item-label header class="project-timeline__conflict-group">
                <span class="project-timeline__conflict-dot" :style="{ backgroundColor: projectColor(group.project) }" />
                <span class="ellipsis">{{ group.project.name }}</span>
                <q-badge
                  rounded
                  :color="warningColor"
                  :label="group.conflicts.length"
                />
              </q-item-label>
              <q-item
                v-for="conflict in group.conflicts"
                :key="conflict.key"
                v-close-popup
                clickable
                class="q-pa-sm"
                @click="goToConflictAsync(conflict)"
              >
                <list-item
                  dense
                  :avatar-icon="SERVICE_TYPE_ICONS[conflict.row.service_type]"
                  :label="conflict.serviceTitle"
                >
                  <template #caption>
                    <span class="project-timeline__conflict-caption">
                      <q-icon :name="ABSENCE_ICON" size="0.875rem" :color="warningColor" />
                      {{ $t('common.overlap') }}: {{ formatRange(conflict) }}
                    </span>
                  </template>
                  <template #right>
                    <q-btn
                      v-close-popup
                      outline
                      rounded
                      dense
                      no-caps
                      size="sm"
                      :color="warningColor"
                      :label="$t('common.resolveConflict')"
                      class="project-timeline__conflict-resolve"
                      @click.stop="openConflict(conflict)"
                    />
                  </template>
                </list-item>
              </q-item>
            </template>
          </q-list>
        </q-menu>
      </q-btn>

      <div class="project-timeline__legend gt-md">
        <span class="project-timeline__legend-item">
          <span class="project-timeline__swatch" />{{ $t('common.confirmed') }}
        </span>
        <span class="project-timeline__legend-item">
          <span class="project-timeline__swatch project-timeline__swatch--unconfirmed" />{{ $t('common.notConfirmed') }}
        </span>
        <span class="project-timeline__legend-item">
          <span class="project-timeline__swatch project-timeline__swatch--planned" />{{ $t('common.planned') }}
        </span>
        <span class="project-timeline__legend-item">
          <span class="project-timeline__swatch project-timeline__swatch--absence" :style="{ '--bar-color': ABSENCE_COLOR }" />{{ $t('common.staff.absence') }}
        </span>
      </div>
      <slot name="toolbar-end" />
    </div>

    <q-linear-progress
      v-if="data.state.loading"
      indeterminate
      color="primary"
      class="project-timeline__loading"
    />
    <div
      ref="scrollRef"
      class="project-timeline__scroll"
      @scroll="onScroll"
      @wheel="onWheel"
      @dragleave="onCanvasDragLeave"
    >
      <div class="project-timeline__canvas" :style="{ width: `${taskListWidth + trackWidth}rem` }">
        <div class="project-timeline__header">
          <div class="project-timeline__corner" :style="{ width: `${taskListWidth}rem` }">
            <span v-if="!taskListCompact" class="project-timeline__corner-title">{{ $t('common.project', 2) }}</span>
            <q-badge
              rounded
              color="primary"
              :label="searchedProjects.length"
              class="project-timeline__corner-badge"
            />
            <template v-if="!taskListCompact">
              <search-input :model-value="projectSearch" class="project-timeline__search" @update:model-value="onProjectSearch" />
              <div class="project-timeline__resizer" @pointerdown="onResizerPointerDown" />
            </template>
          </div>
          <div class="project-timeline__scale" :style="{ width: `${trackWidth}rem` }" @pointerdown="onPanStart">
            <project-timeline-scale
              :range-start="rangeStart"
              :range-days="rangeDays"
              :day-width="dayWidth"
              :sticky-offset="taskListWidth"
            />
          </div>
        </div>

        <div class="project-timeline__body" @contextmenu.capture="contextTarget = null">
          <div class="project-timeline__grid" :style="gridStyle" />
          <div v-if="dayWidth < HOUR_LINES_MIN_WIDTH" class="project-timeline__today-band" :style="todayBandStyle" />
          <div class="project-timeline__today" :style="{ left: `${todayLeft}rem` }" />

          <div
            v-for="vr in visibleRows"
            :key="vr.key"
            :class="rowClasses(vr)"
            :style="{ '--project-color': projectColor(vr.project) }"
            :data-row-key="vr.key"
            @click="selectRow(vr)"
            @contextmenu="onRowContextMenu($event, vr)"
            @touchstart.passive="onRowTouchStart($event, vr)"
            @dragover="onRowDragOver($event, vr)"
            @drop="onRowDropAsync($event, vr)"
          >
            <div
              :class="['project-timeline__cell', { 'row-highlight': highlightedRowId === vr.key }]"
              :style="{ width: `${taskListWidth}rem` }"
            >
              <div class="project-timeline__accent" />

              <template v-if="vr.kind === PROJECT">
                <q-btn
                  flat
                  round
                  dense
                  size="sm"
                  color="grey-7"
                  :icon="expanded.has(vr.key) ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                  class="project-timeline__chevron"
                  @click.stop="toggleExpanded(vr.key)"
                />
                <div class="project-timeline__avatar project-timeline__avatar--project">
                  <q-icon name="mdi-movie-open-outline" size="1rem" />
                  <q-tooltip v-if="taskListCompact && !isMobile">{{ vr.project.name }}</q-tooltip>
                </div>
                <div class="project-timeline__titles">
                  <div class="project-timeline__title project-timeline__title--project">
                    {{ vr.project.name }}
                  </div>
                  <div class="project-timeline__subtitle">
                    {{ vr.project.listing_name }} · {{ vr.summary.rowCount }} {{ $t('common.service', vr.summary.rowCount) }}
                  </div>
                </div>
                <q-chip
                  v-if="vr.conflicts.length"
                  dense
                  clickable
                  :color="warningColor"
                  text-color="white"
                  icon="mdi-alert"
                  :label="vr.conflicts.length"
                  class="project-timeline__conflict-chip"
                  @click.stop="goToConflictAsync(vr.conflicts[0])"
                >
                  <q-tooltip>{{ vr.conflicts.length }} {{ $t('common.conflict', vr.conflicts.length) }}</q-tooltip>
                </q-chip>
                <q-icon
                  v-if="vr.project.lock_planned"
                  name="mdi-lock"
                  size="1rem"
                  color="warning"
                >
                  <q-tooltip>{{ $t('common.lockPlanned') }}</q-tooltip>
                </q-icon>
                <div class="project-timeline__progress">
                  <q-circular-progress
                    :value="vr.summary.confirmedPercent"
                    size="1.375rem"
                    :thickness="0.25"
                    color="positive"
                    track-color="grey-3"
                    show-value
                  >
                    <q-icon
                      v-if="vr.summary.confirmed"
                      name="mdi-check"
                      size="0.75rem"
                      color="positive"
                    />
                  </q-circular-progress>
                  <q-tooltip max-width="18rem">
                    <div class="text-weight-bold">
                      {{ $t('common.confirmedAllocationsProgress', {
                        confirmed: vr.summary.confirmedCount,
                        total: vr.summary.rowCount,
                        percent: vr.summary.confirmedPercent,
                      }) }}
                    </div>
                    <div>{{ $t('common.confirmedAllocationsProgressHint') }}</div>
                  </q-tooltip>
                </div>
              </template>

              <template v-else-if="vr.kind === SERVICE">
                <div class="project-timeline__indent" :style="{ width: `${vr.depth * 1.25}rem` }" />
                <q-btn
                  flat
                  round
                  dense
                  size="sm"
                  color="grey-6"
                  :icon="expanded.has(vr.key) ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                  class="project-timeline__chevron"
                  @click.stop="toggleExpanded(vr.key)"
                />
                <div class="project-timeline__avatar">
                  <img
                    v-if="vr.row.service_avatar && !unloadableAvatars.has(vr.row.service_avatar)"
                    :src="vr.row.service_avatar"
                    class="project-timeline__avatar-image"
                    @error="unloadableAvatars.add(vr.row.service_avatar)"
                  >
                  <q-icon v-else :name="serviceIcon(vr)" size="0.875rem" />
                  <q-tooltip v-if="taskListCompact && !isMobile">{{ rowTitle(vr.row) }}</q-tooltip>
                </div>
                <div class="project-timeline__titles">
                  <div class="project-timeline__title">
                    {{ rowTitle(vr.row) }}
                  </div>
                  <div v-if="hasListingText(vr.row)" class="project-timeline__subtitle">
                    {{ vr.row.service_title }}
                  </div>
                </div>
                <q-icon
                  v-if="vr.conflicts.length"
                  name="mdi-alert"
                  size="1.125rem"
                  :color="warningColor"
                  class="project-timeline__conflict-icon cursor-pointer"
                  @click.stop="openConflict(vr.conflicts[0])"
                >
                  <q-tooltip>{{ $t('common.availabilityConflict') }} · {{ $t('common.resolveConflict') }}</q-tooltip>
                </q-icon>
                <span class="project-timeline__meta">{{ formatDecimal(vr.row.quantity) }} × {{ formatDecimal(vr.row.amount_per_unit) }} {{ vr.row.uom_symbol }}</span>
                <q-icon
                  v-if="vr.project.lock_planned"
                  name="mdi-lock"
                  size="0.875rem"
                  color="warning"
                >
                  <q-tooltip>{{ $t('common.plannedLocked') }}</q-tooltip>
                </q-icon>
                <q-icon
                  :name="vr.summary.confirmed ? 'mdi-check-circle' : 'mdi-progress-clock'"
                  :color="vr.summary.confirmed ? 'positive' : 'grey-5'"
                  size="1rem"
                >
                  <q-tooltip>{{ vr.summary.confirmed ? $t('common.confirmed') : $t('common.notConfirmed') }}</q-tooltip>
                </q-icon>
              </template>

              <template v-else>
                <div class="project-timeline__indent" :style="{ width: `${vr.depth * 1.25 + 2.25}rem` }" />
                <div class="project-timeline__lane-label">
                  <q-icon :name="vr.kind === EVENTS ? 'mdi-calendar-clock' : ABSENCE_ICON" size="0.875rem" />
                  <span class="project-timeline__lane-name">{{ vr.kind === EVENTS ? $t('common.event', 2) : $t('common.absences') }}</span>
                  <span class="project-timeline__lane-count">{{ vr.count }}</span>
                  <q-icon
                    v-if="vr.conflicts.length"
                    name="mdi-alert"
                    size="0.875rem"
                    :color="warningColor"
                  />
                </div>
                <q-btn
                  flat
                  round
                  dense
                  size="xs"
                  color="primary"
                  icon="mdi-plus"
                  class="project-timeline__lane-add"
                  @click.stop="onLaneAdd(vr)"
                >
                  <q-tooltip>{{ $t('common.add') }}</q-tooltip>
                </q-btn>
              </template>
            </div>

            <div
              :class="['project-timeline__track', { 'row-highlight': highlightedRowId === vr.key }]"
              :style="{ width: `${trackWidth}rem` }"
              @pointerdown="onPanStart"
              @dblclick="onTrackDblClick($event, vr)"
            >
              <div
                v-for="conflict in vr.conflicts"
                :key="`cz-${conflict.key}`"
                class="project-timeline__conflict-zone"
                :style="spanStyle(conflict)"
              />
              <div
                v-for="conflict in vr.kind === SERVICE || vr.kind === PROJECT ? vr.conflicts : []"
                :key="`cb-${conflict.key}`"
                class="project-timeline__conflict-badge"
                :style="{ left: `calc(${spanStyle(conflict).left} + ${spanStyle(conflict).width} / 2)` }"
                @pointerdown.stop
                @click.stop="openConflict(conflict)"
              >
                <q-icon name="mdi-alert" size="0.75rem" />
                <q-tooltip>
                  <div class="text-weight-bold">
                    {{ $t('common.availabilityConflict') }}
                  </div>
                  <div>{{ conflict.serviceTitle }} · {{ formatRange(conflict) }}</div>
                </q-tooltip>
              </div>
              <div
                v-if="plannedBox(vr)"
                class="project-timeline__planned"
                :style="{ ...spanStyle(plannedBox(vr)), '--bar-color': vr.bars[0]?.color }"
              />
              <div
                v-for="bar in vr.bars"
                :key="bar.key"
                :class="barClasses(bar)"
                :style="barStyle(bar)"
                :data-bar-key="bar.key"
                @pointerdown="onBarPointerDown($event, vr, bar, MOVE)"
                @click.stop="selectBar(vr, bar)"
                @dblclick.stop="onBarDblClick(vr, bar)"
              >
                <div class="project-timeline__handle project-timeline__handle--start" @pointerdown="onBarPointerDown($event, vr, bar, RESIZE_START)" />
                <q-icon
                  v-if="bar.conflict && !bar.isProject"
                  name="mdi-alert"
                  size="0.875rem"
                  class="project-timeline__bar-alert"
                />
                <q-icon
                  v-if="bar.icon"
                  :name="bar.icon"
                  size="0.875rem"
                  class="project-timeline__bar-icon"
                />
                <span class="project-timeline__bar-label">{{ bar.label }}</span>
                <span v-if="bar.availabilityColor" class="project-timeline__availability" :style="{ backgroundColor: bar.availabilityColor }" />
                <div class="project-timeline__handle project-timeline__handle--end" @pointerdown="onBarPointerDown($event, vr, bar, RESIZE_END)" />
                <q-tooltip v-if="!drag && !isMobile" :delay="400" class="project-timeline__tooltip">
                  <div class="text-weight-bold">{{ bar.tooltip }}</div>
                  <div>{{ formatRange(barRange(bar)) }}</div>
                  <div v-if="bar.availabilityLabel" class="project-timeline__tooltip-availability">
                    <span class="project-timeline__availability" :style="{ backgroundColor: bar.availabilityColor }" />
                    {{ bar.availabilityLabel }}
                  </div>
                </q-tooltip>
              </div>
              <div
                v-if="dragLabel(vr)"
                class="project-timeline__drag-label"
                :style="{ left: spanStyle(dragLabel(vr)).left }"
              >
                {{ formatRange(dragLabel(vr)) }}
              </div>
            </div>
          </div>

          <q-menu
            context-menu
            touch-position
            auto-close
          >
            <q-list dense class="project-timeline__menu">
              <q-item-label v-if="contextTitle" header class="project-timeline__menu-header">
                <div class="project-timeline__menu-title">
                  {{ contextTitle }}
                </div>
                <div v-if="contextTarget?.bar" class="project-timeline__menu-caption">
                  {{ formatRange(contextTarget.bar) }}
                </div>
              </q-item-label>
              <template v-for="(section, idx) in contextSections" :key="idx">
                <q-separator v-if="idx > 0 || contextTitle" />
                <q-item
                  v-for="item in section"
                  :key="item.key"
                  clickable
                  @click="item.action"
                >
                  <q-item-section avatar class="project-timeline__menu-icon">
                    <q-icon :name="item.icon" :color="item.color ?? 'grey-8'" size="1.125rem" />
                  </q-item-section>
                  <q-item-section :class="item.color ? `text-${item.color}` : undefined">
                    {{ item.label }}
                  </q-item-section>
                </q-item>
              </template>
            </q-list>
          </q-menu>

          <div class="project-timeline__filler">
            <div class="project-timeline__cell" :style="{ width: `${taskListWidth}rem` }" />
          </div>
        </div>
      </div>
    </div>

    <project-reschedule-dialogs ref="rescheduleDialogs" />

    <no-stock-confirm-dialog v-model="noStockDialog" />

    <absence-conflict-dialog
      v-model="data.absenceConflictDialog.value.show"
      :conflicts="data.absenceConflictDialog.value.conflicts"
      @remove="data.absenceConflictDialog.value.onRemove()"
    />

    <allocation-confirm-dialog
      v-if="allocationToConfirm"
      :model-value="true"
      :service-id="allocationToConfirm.serviceId"
      :project-id="allocationToConfirm.projectId"
      :budget-document-row-id="allocationToConfirm.budgetDocumentRowId"
      :service-name="allocationToConfirm.serviceName"
      :quantity="allocationToConfirm.quantity"
      :project-start="allocationToConfirm.projectStart"
      @update:model-value="value => { if (!value) allocationToConfirm = null }"
      @confirmed="onAllocationConfirmed"
    />

    <group-components-dialog
      :model-value="groupToAssign !== null"
      :service="groupToAssign?.service ?? null"
      @update:model-value="value => { if (!value) groupToAssign = null }"
      @confirm="onGroupComponentsConfirm"
    />

    <q-dialog
      :model-value="quantityToAssign !== null"
      class="dialog-xs"
      @update:model-value="value => { if (!value) quantityToAssign = null }"
    >
      <q-card v-if="quantityToAssign">
        <q-card-section class="row items-center no-wrap q-gutter-sm">
          <q-avatar
            :icon="SERVICE_TYPE_ICONS[quantityToAssign.service.type]"
            color="primary"
            text-color="white"
            size="md"
          />
          <span class="text-h6 ellipsis">{{ planningStore.getServiceName(quantityToAssign.service) }}</span>
        </q-card-section>
        <q-card-section class="q-pt-none">
          <q-input
            v-model.number="quantityToAssign.quantity"
            type="number"
            :min="1"
            :max="quantityToAssign.max"
            :label="$t('common.quantity')"
            autofocus
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat :label="$t('common.cancel')" @click="quantityToAssign = null" />
          <q-btn
            color="primary"
            :label="$t('common.confirm')"
            :disable="!(quantityToAssign.quantity >= 1 && (!quantityToAssign.max || quantityToAssign.quantity <= quantityToAssign.max))"
            @click="onQuantityConfirm"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <delete-options-dialog
      :model-value="projectBounds !== null"
      :title="$t('common.projectBoundsExceeded')"
      :message="$t('common.extendProjectDatesMessage')"
      icon="mdi-calendar-expand-horizontal"
      icon-color="info"
      :options="projectBoundsOptions"
      @select="onProjectBoundsSelect"
      @cancel="projectBounds = null"
      @update:model-value="value => { if (!value) projectBounds = null }"
    />

    <confirm-dialog
      :model-value="pendingDelete !== null"
      :title="$t('common.confirmDelete')"
      :message="pendingDelete?.message ?? ''"
      :label="$t('common.delete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteConfirmed"
      @cancel="pendingDelete = null"
      @update:model-value="pendingDelete = null"
    />

    <confirm-dialog
      :model-value="pendingDuplicate !== null"
      :message="$t('warning.serviceAlreadyInProjectConfirm')"
      icon="mdi-alert"
      :icon-color="warningColor"
      @confirm="onDuplicateConfirmed"
      @cancel="pendingDuplicate = null"
      @update:model-value="pendingDuplicate = null"
    />

    <project-timeline-replace-dialog
      :model-value="replacing"
      @replace="onReplace"
      @cancel="replacing = null"
    />

    <project-timeline-conflict-dialog
      :model-value="conflictToResolve"
      :format-range="formatRange"
      @resolve="onConflictResolve"
      @cancel="conflictToResolve = null"
    />

    <project-timeline-event-dialog
      :model-value="editing?.event ?? null"
      :title="editingTitle"
      :icon="editingIcon"
      :show-confirmed="editing?.event.type === ABSENCE && canApproveAbsences"
      @save="onEventSave"
      @delete="onEventDelete"
      @cancel="editing = null"
    />
  </div>
</template>

<style scoped lang="scss">
.project-timeline {
  position: relative;
  --timeline-surface: #{$white};
  --timeline-surface-alt: color-mix(in srgb, var(--q-primary) 3%, var(--timeline-surface));
  --timeline-border: color-mix(in srgb, var(--q-primary) 15%, transparent);
  --timeline-line: rgba(0, 0, 0, 0.06);
  --timeline-line-strong: rgba(0, 0, 0, 0.1);
  --timeline-line-faint: rgba(0, 0, 0, 0.03);
  --timeline-weekend: color-mix(in srgb, var(--q-primary) 4%, transparent);
  --timeline-text: #1f1d2b;
  --timeline-text-muted: #8b879a;
  --timeline-today: var(--q-secondary);
  --timeline-tooltip-bg: #1f1d2b;
  --timeline-conflict: var(--q-warning);
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--timeline-surface);
  border: 1px solid var(--timeline-border);
  border-radius: $app-border-radius;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  color: var(--timeline-text);
}

:global(.body--dark) .project-timeline {
  --timeline-surface: #{$dark};
  --timeline-border: color-mix(in srgb, var(--q-primary) 30%, transparent);
  --timeline-line: rgba(255, 255, 255, 0.07);
  --timeline-line-strong: rgba(255, 255, 255, 0.12);
  --timeline-line-faint: rgba(255, 255, 255, 0.035);
  --timeline-weekend: color-mix(in srgb, var(--q-primary) 8%, transparent);
  --timeline-text: #ecebf2;
  --timeline-text-muted: #9a96aa;
  --timeline-tooltip-bg: #3a3748;
}

// --- Toolbar ---
.project-timeline__toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--timeline-surface-alt);
  border-bottom: 1px solid var(--timeline-border);
}

.project-timeline__presets {
  border: 1px solid var(--timeline-border);
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--timeline-surface);

  :deep(.q-btn) {
    padding: 0.125rem 0.75rem;
    font-size: 0.8125rem;
  }
}

.project-timeline__zoom {
  display: flex;
  border: 1px solid var(--timeline-border);
  border-radius: 0.5rem;
  background: var(--timeline-surface);
}

.project-timeline__today-btn {
  padding: 0 0.75rem;
  font-size: 0.8125rem;
}

.project-timeline__legend {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-right: 0.5rem;
  font-size: 0.75rem;
  color: var(--timeline-text-muted);
}

.project-timeline__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

.project-timeline__swatch {
  --bar-color: var(--q-primary);
  width: 1.25rem;
  height: 0.625rem;
  border-radius: 0.25rem;
  background: var(--bar-color);
}

.project-timeline__swatch--unconfirmed {
  background: repeating-linear-gradient(
    135deg,
    color-mix(in srgb, var(--bar-color) 30%, var(--timeline-surface)) 0 0.1875rem,
    color-mix(in srgb, var(--bar-color) 12%, var(--timeline-surface)) 0.1875rem 0.375rem
  );
  border: 1px dashed color-mix(in srgb, var(--bar-color) 60%, transparent);
}

.project-timeline__swatch--planned {
  background: transparent;
  border: 1px dashed var(--timeline-text-muted);
}

// --- Scroll area ---
.project-timeline__scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--q-primary) 30%, transparent) transparent;
}

.gantt--panning,
.gantt--panning :deep(*) {
  cursor: grabbing !important;
}

.project-timeline__canvas {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

// --- Header ---
.project-timeline__header {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  flex-shrink: 0;
  height: 3.5rem;
  background: var(--timeline-surface);
  border-bottom: 1px solid var(--timeline-border);
}

.project-timeline__corner {
  position: sticky;
  left: 0;
  z-index: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1rem;
  background: var(--timeline-surface);
  border-right: 1px solid var(--timeline-border);
}

.project-timeline__search {
  flex: 0 1 14rem;
  margin-left: auto;
  background: var(--timeline-surface);
}

.project-timeline__corner-title {
  font-size: 0.875rem;
  font-weight: 600;
}

.project-timeline__resizer {
  position: absolute;
  top: 0;
  right: -0.25rem;
  bottom: 0;
  width: 0.5rem;
  cursor: col-resize;
  z-index: 2;

  &:hover {
    background: linear-gradient(to right, transparent 0.1875rem, var(--q-primary) 0.1875rem 0.3125rem, transparent 0.3125rem);
  }
}

.project-timeline__scale {
  flex-shrink: 0;
  cursor: grab;
}

// --- Body ---
.project-timeline__body {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.project-timeline__grid {
  position: absolute;
  top: 0;
  bottom: 0;
  pointer-events: none;
}

.project-timeline__today-band {
  position: absolute;
  top: 0;
  bottom: 0;
  background: color-mix(in srgb, var(--q-primary) 5%, transparent);
  pointer-events: none;
}

.project-timeline__today {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 2px solid var(--timeline-today);
  z-index: 2;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -0.3125rem;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: var(--timeline-today);
  }
}

.project-timeline__filler {
  --row-bg: var(--timeline-surface);
  flex: 1;
  display: flex;
}

// --- Rows ---
.project-timeline__row {
  --row-tint-color: var(--q-primary);
  --row-tint: 0%;
  --row-bg: color-mix(in srgb, var(--row-tint-color) var(--row-tint), var(--timeline-surface));
  display: flex;
  min-height: 2.75rem;
  background: var(--row-bg);
  transition: background-color 0.15s ease;

  &:hover {
    --row-tint: 4%;
  }
}

.project-timeline__row--project {
  --row-tint-color: var(--project-color);
  --row-tint: 7%;
  min-height: 3rem;
  border-top: 1px solid var(--timeline-line-strong);

  &:hover {
    --row-tint: 10%;
  }
}

.project-timeline__row--service {
  border-top: 1px solid var(--timeline-line-faint);
}

.project-timeline__row--lane {
  --row-tint: 2%;
  min-height: 2rem;
}

.project-timeline__row--search-match,
.project-timeline__row--search-match:hover {
  --row-tint-color: var(--q-primary);
  --row-tint: 10%;
}

.project-timeline__row--selected,
.project-timeline__row--selected:hover {
  --row-tint-color: var(--q-primary);
  --row-tint: 9%;
}

.project-timeline__row--drop,
.project-timeline__row--drop:hover {
  --row-tint-color: var(--q-primary);
  --row-tint: 16%;
}

.project-timeline__cell {
  position: sticky;
  left: 0;
  z-index: 3;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0 0.75rem 0 0.5rem;
  background: var(--row-bg);
  border-right: 1px solid var(--timeline-border);
  font-size: 0.8125rem;
}

.project-timeline__accent {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 0.1875rem;
  background: var(--project-color);
  opacity: 0.35;
}

.project-timeline__row--project .project-timeline__accent {
  width: 0.25rem;
  opacity: 1;
}

.project-timeline__indent {
  flex-shrink: 0;
}

.project-timeline__chevron {
  flex-shrink: 0;
}

.project-timeline__avatar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.625rem;
  height: 1.625rem;
  border-radius: 50%;
  background: color-mix(in srgb, var(--project-color) 14%, var(--timeline-surface));
  color: var(--project-color);
}

.project-timeline__avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.project-timeline__avatar--project {
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 0.5rem;
  background: var(--project-color);
  color: white;
}

.project-timeline__titles {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.project-timeline__title,
.project-timeline__subtitle {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.project-timeline__title--project {
  font-weight: 600;
}

.project-timeline__subtitle {
  font-size: 0.6875rem;
  color: var(--timeline-text-muted);
}

.project-timeline__meta {
  flex-shrink: 0;
  padding: 0.0625rem 0.375rem;
  border-radius: 0.375rem;
  background: var(--timeline-surface-alt);
  font-size: 0.6875rem;
  color: var(--timeline-text-muted);
  white-space: nowrap;
}

.project-timeline__progress {
  flex-shrink: 0;
  display: flex;
  cursor: help;
}

.project-timeline__lane-label {
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--timeline-text-muted);
}

.project-timeline__lane-count {
  padding: 0 0.375rem;
  border-radius: 1rem;
  background: color-mix(in srgb, var(--project-color) 12%, var(--timeline-surface));
  font-size: 0.6875rem;
  color: var(--timeline-text);
}

.project-timeline__lane-add {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.project-timeline__row:hover .project-timeline__lane-add {
  opacity: 1;
}

.project-timeline--mobile {
  .project-timeline__toolbar {
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    overflow-x: auto;
    scrollbar-width: none;

    > * {
      flex-shrink: 0;
    }
  }

  .project-timeline__body {
    user-select: none;
    -webkit-touch-callout: none;
  }

  .project-timeline__lane-add {
    opacity: 1;
  }

  .project-timeline__bar--selected .project-timeline__handle {
    opacity: 1;
    width: 0.375rem;

    &::after {
      inset: -0.5rem -0.75rem;
    }
  }
}

.project-timeline--compact {
  .project-timeline__corner {
    justify-content: center;
    padding: 0 0.5rem;
  }

  .project-timeline__cell > :not(.project-timeline__accent, .project-timeline__chevron, .project-timeline__avatar, .project-timeline__lane-label),
  .project-timeline__lane-name,
  .project-timeline__lane-count {
    display: none;
  }

  .project-timeline__lane-label {
    justify-content: flex-end;
  }
}

// --- Track & bars ---
.project-timeline__track {
  position: relative;
  flex-shrink: 0;
  cursor: grab;
}

.project-timeline__planned {
  position: absolute;
  top: 50%;
  height: 1.75rem;
  transform: translateY(-50%);
  border: 1.5px dashed color-mix(in srgb, var(--bar-color) 55%, transparent);
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--bar-color) 5%, transparent);
  pointer-events: none;
}

.project-timeline__bar {
  --bar-color: var(--q-primary);
  position: absolute;
  top: 50%;
  height: 1.625rem;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0 0.625rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, var(--bar-color), color-mix(in srgb, var(--bar-color) 60%, white));
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow:
    0 1px 2px color-mix(in srgb, var(--bar-color) 30%, transparent),
    0 0.25rem 0.5rem -0.25rem color-mix(in srgb, var(--bar-color) 45%, transparent);
  overflow: hidden;
  cursor: grab;
  user-select: none;
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    box-shadow:
      0 1px 2px color-mix(in srgb, var(--bar-color) 35%, transparent),
      0 0.375rem 0.875rem -0.25rem color-mix(in srgb, var(--bar-color) 60%, transparent);
  }

  &:hover .project-timeline__handle {
    opacity: 1;
  }
}

.project-timeline__bar--unconfirmed {
  background: repeating-linear-gradient(
    135deg,
    color-mix(in srgb, var(--bar-color) 24%, var(--timeline-surface)) 0 0.375rem,
    color-mix(in srgb, var(--bar-color) 12%, var(--timeline-surface)) 0.375rem 0.75rem
  );
  border: 1px dashed color-mix(in srgb, var(--bar-color) 60%, transparent);
  color: color-mix(in srgb, var(--bar-color) 75%, var(--timeline-text));
  box-shadow: none;

  .project-timeline__handle {
    background: color-mix(in srgb, var(--bar-color) 55%, transparent);
  }
}

.project-timeline__bar--project {
  height: 1.375rem;
  background: color-mix(in srgb, var(--bar-color) 22%, var(--timeline-surface));
  border: 1px solid color-mix(in srgb, var(--bar-color) 45%, transparent);
  color: color-mix(in srgb, var(--bar-color) 70%, var(--timeline-text));
  font-weight: 600;
  box-shadow: none;
}

.project-timeline__row--lane .project-timeline__bar {
  height: 1.25rem;
  gap: 0.25rem;
  padding: 0 0.375rem;
  border-radius: 0.375rem;
  font-size: 0.6875rem;
}

.project-timeline__bar--selected {
  touch-action: none;
}

.project-timeline__bar--selected,
.project-timeline__bar--selected:hover {
  box-shadow:
    0 0 0 0.125rem var(--timeline-surface),
    0 0 0 0.25rem var(--q-primary);
}

.project-timeline__bar--dragging {
  z-index: 1;
  cursor: grabbing;
  transform: translateY(-50%) scale(1.02);
  box-shadow: 0 0.5rem 1.25rem -0.25rem color-mix(in srgb, var(--bar-color) 70%, transparent);
}

.project-timeline__bar-icon {
  flex-shrink: 0;
}

.project-timeline__bar-label {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.project-timeline__availability {
  flex-shrink: 0;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  box-shadow: 0 0 0 0.125rem var(--timeline-surface);
}

.project-timeline__handle {
  position: absolute;
  top: 0.3125rem;
  bottom: 0.3125rem;
  width: 0.25rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.75);
  opacity: 0;
  cursor: ew-resize;
  z-index: 1;
  transition: opacity 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    inset: -0.3125rem -0.1875rem;
  }
}

.project-timeline__handle--start {
  left: 0.1875rem;
}

.project-timeline__handle--end {
  right: 0.1875rem;
}

.project-timeline__bar--conflict,
.project-timeline__bar--conflict:hover {
  box-shadow:
    0 0 0 0.125rem var(--timeline-surface),
    0 0 0 0.25rem var(--timeline-conflict);
}

.project-timeline__bar-alert {
  flex-shrink: 0;
  color: var(--timeline-conflict);
  filter: drop-shadow(0 0 0.0625rem rgba(0, 0, 0, 0.4));
}

.project-timeline__conflict-zone {
  position: absolute;
  top: 0;
  bottom: 0;
  background: repeating-linear-gradient(
    135deg,
    color-mix(in srgb, var(--timeline-conflict) 22%, transparent) 0 0.3125rem,
    color-mix(in srgb, var(--timeline-conflict) 8%, transparent) 0.3125rem 0.625rem
  );
  border-left: 2px solid var(--timeline-conflict);
  border-right: 2px solid var(--timeline-conflict);
  pointer-events: none;
}

.project-timeline__conflict-badge {
  position: absolute;
  top: 0.125rem;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  transform: translateX(-50%);
  border-radius: 50%;
  background: var(--timeline-conflict);
  color: white;
  box-shadow: 0 0 0 0.125rem var(--timeline-surface);
  cursor: pointer;
  animation: project-timeline-pulse 1.8s ease-out infinite;
}

@keyframes project-timeline-pulse {
  0% {
    box-shadow:
      0 0 0 0.125rem var(--timeline-surface),
      0 0 0 0.125rem color-mix(in srgb, var(--timeline-conflict) 60%, transparent);
  }
  70%,
  100% {
    box-shadow:
      0 0 0 0.125rem var(--timeline-surface),
      0 0 0 0.625rem transparent;
  }
}

.project-timeline__row--conflict .project-timeline__accent {
  width: 0.25rem;
  background: var(--timeline-conflict);
  opacity: 1;
}

.project-timeline__conflict-chip {
  flex-shrink: 0;
  height: 1.25rem;
  margin: 0;
  padding: 0 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;

  :deep(.q-chip__icon) {
    margin-right: 0.125rem;
    font-size: 0.875rem;
  }
}

.project-timeline__loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 6;
}

.project-timeline__conflict-icon {
  flex-shrink: 0;
}

.project-timeline__conflicts-btn {
  padding: 0 0.75rem;
  font-size: 0.8125rem;
  animation: project-timeline-pulse 1.8s ease-out infinite;
}

.project-timeline__conflict-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: color-mix(in srgb, var(--timeline-conflict) 10%, var(--timeline-surface));
  border-bottom: 1px solid color-mix(in srgb, var(--timeline-conflict) 25%, transparent);
}

.project-timeline__conflict-list {
  width: 28rem;
  max-width: 92vw;
  max-height: 60vh;
  overflow-y: auto;
  padding-bottom: 0.5rem;
}

.project-timeline__conflict-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem 0.25rem;
  font-weight: 600;
  color: var(--timeline-text);
}

.project-timeline__conflict-dot {
  flex-shrink: 0;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
}

.project-timeline__conflict-caption {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.project-timeline__conflict-resolve {
  padding: 0 0.625rem;
}

.project-timeline__bar--focused {
  animation: project-timeline-focus 0.6s ease-in-out 3;
}

@keyframes project-timeline-focus {
  50% {
    box-shadow:
      0 0 0 0.1875rem var(--timeline-surface),
      0 0 0 0.5rem color-mix(in srgb, var(--q-primary) 45%, transparent);
  }
}

.project-timeline__menu {
  min-width: 15rem;
  max-width: 22rem;
}

.project-timeline__menu-header {
  padding: 0.5rem 1rem;
}

.project-timeline__menu-title {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: 600;
  color: var(--timeline-text);
}

.project-timeline__menu-caption {
  font-size: 0.6875rem;
}

.project-timeline__menu-icon {
  min-width: 2rem;
}

.project-timeline__tooltip-availability {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.25rem;
}

.project-timeline__drag-label {
  position: absolute;
  bottom: calc(50% + 1.125rem);
  z-index: 4;
  padding: 0.1875rem 0.5rem;
  border-radius: 0.375rem;
  background: var(--timeline-tooltip-bg);
  color: white;
  font-size: 0.6875rem;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.2);
}
</style>
